import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import YearSelector from '../../../Common/Selectors/YearSelector';
import { creators as OpportunityActions } from '../../../Reducers/Reporting/Opportunity';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import TopRightChart from './TopRightChart';
import BottomChart from './BottomChart';
import BottomRightChart from './BottomRightChart';

const Opportunities = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  opportunitiesSetting,
  selectedCompany,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  const opportunitiesSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.opportunity ?? '');
  const summaryData = useSelector((state) => state.Reporting.Opportunity.summaryData);
  const detailData = useSelector((state) => state.Reporting.Opportunity.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [label, setLabel] = useState('');
  const [selectedTopLeftItems, setSelectedTopLeftItems] = useState([]);
  const [selectedTopLeftLabels, setSelectedTopLeftLabels] = useState([]);
  const [selectedTopRightItems, setSelectedTopRightItems] = useState([]);
  const [Probability, setProbability] = useState('All');
  const [selectedSt, setSelectedSt] = useState('All');
  const [searchStr, setSearchStr] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedDirector, setSelectedDirector] = useState('All');
  const [searchRightStr, setSearchRightStr] = useState('');
  const [resize, setResize] = useState(false);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  useEffect(() => {
    const initializeState = async () => {
      setSelectedYears([defaultYear]);
      setLabel(defaultYear.toString());
      setSelectedTopLeftLabels(
        appdata.getLocalItem('oppSelectedTopLeftLabels')
          ? appdata.getLocalItem('oppSelectedTopLeftLabels')
          : [financialMonth(defaultMonth, defaultStartMonth) - 1]
      );
      setSelectedTopLeftItems(
        appdata.getLocalItem('oppSelectedTopLeftItems')
          ? appdata.getLocalItem('oppSelectedTopLeftItems')
          : [{ month: financialMonth(defaultMonth, defaultStartMonth) - 1 }]
      );
      setSelectedTopRightItems(
        appdata.getLocalItem('oppSelectedTopRightItems')
          ? appdata.getLocalItem('oppSelectedTopRightItems')
          : []
      );

      if (!opportunitiesSetting) {
        await dispatch(ReportSettingActions.reportSettingUpdateRequest({
          Opportunities: JSON.stringify({
            Probability: 'All',
            selectedSt: 'All',
            searchStr: '',
            selectedType: 'All',
            selectedDirector: 'All',
            searchRightStr: '',
          }),
        }));
      } else {
        const settings = JSON.parse(opportunitiesSetting);
        setProbability(settings.Probability);
        setSelectedSt(settings.selectedSt);
        setSearchStr(settings.searchStr);
        setSelectedType(settings.selectedType);
        setSelectedDirector(settings.selectedDirector);
        setSearchRightStr(settings.searchRightStr);
      }
    };

    initializeState();
  }, []);

  const triggerOpportunityRequests = useCallback(() => {
    dispatch(OpportunityActions.opportunitySummaryRequest([defaultYear]));
    dispatch(OpportunityActions.opportunityDetailRequest([defaultYear]));
  }, [defaultYear, dispatch]);

  useEffect(() => {
    const updateState = async () => {
      setSelectedYears([defaultYear]);
      setLabel(defaultYear.toString());
      setSelectedTopLeftLabels([financialMonth(defaultMonth, defaultStartMonth) - 1]);
      setSelectedTopLeftItems([{ month: financialMonth(defaultMonth, defaultStartMonth) - 1 }]);
      setSelectedTopRightItems([]);

      if (!opportunitiesSetting) {
        await dispatch(ReportSettingActions.reportSettingUpdateRequest({
          Opportunities: JSON.stringify({
            Probability: 'All',
            selectedSt: 'All',
            searchStr: '',
            selectedType: 'All',
            selectedDirector: 'All',
            searchRightStr: '',
          }),
        }));
      }

      const missingDefaults = !lastFetchedCompany || !lastFetchedDimDate;
      if (missingDefaults) {
        triggerOpportunityRequests();
        return;
      }

      const valuesChanged =
        lastFetchedCompany !== selectedCompany ||
        lastFetchedDimDate !== defaultDimDate;

      if (valuesChanged) {
        triggerOpportunityRequests();
      }
    };

    updateState();
  }, [defaultYear, defaultMonth, defaultDimDate, selectedCompany, opportunitiesSetting, lastFetchedCompany, lastFetchedDimDate, dispatch, triggerOpportunityRequests]);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedYears) setSelectedYears(event.selectedYears);
    if (event.label) setLabel(event.label);
    if (event.selectedTopLeftItems) {
      setSelectedTopLeftItems(event.selectedTopLeftItems);
      appdata.setLocalItem('oppSelectedTopLeftItems', event.selectedTopLeftItems);
    }
    if (event.selectedTopLeftLabels) {
      setSelectedTopLeftLabels(event.selectedTopLeftLabels);
      appdata.setLocalItem('oppSelectedTopLeftLabels', event.selectedTopLeftLabels);
    }
    if (event.selectedTopRightItems) {
      setSelectedTopRightItems(event.selectedTopRightItems);
      appdata.setLocalItem('oppSelectedTopRightItems', event.selectedTopRightItems);
    }

    const nextProbability = event.Probability !== undefined ? event.Probability : Probability;
    const nextSelectedSt = event.selectedSt !== undefined ? event.selectedSt : selectedSt;
    const nextSearchStr = event.searchStr !== undefined ? event.searchStr : searchStr;
    const nextSelectedType = event.selectedType !== undefined ? event.selectedType : selectedType;
    const nextSelectedDirector = event.selectedDirector !== undefined ? event.selectedDirector : selectedDirector;
    const nextSearchRightStr = event.searchRightStr !== undefined ? event.searchRightStr : searchRightStr;

    setProbability(nextProbability);
    setSelectedSt(nextSelectedSt);
    setSearchStr(nextSearchStr);
    setSelectedType(nextSelectedType);
    setSelectedDirector(nextSelectedDirector);
    setSearchRightStr(nextSearchRightStr);

    await dispatch(ReportSettingActions.reportSettingUpdateRequest({
      Opportunities: JSON.stringify({
        Probability: nextProbability,
        selectedSt: nextSelectedSt,
        searchStr: nextSearchStr,
        selectedType: nextSelectedType,
        selectedDirector: nextSelectedDirector,
        searchRightStr: nextSearchRightStr,
      }),
    }));
  }, [Probability, selectedSt, searchStr, selectedType, selectedDirector, searchRightStr, dispatch]);

  const handleYear = useCallback(async (event) => {
    if (event.selectedYears.length === 0) {
      toast.warning('Please select the year.');
    }

    dispatch(OpportunityActions.opportunitySummaryRequest(event.selectedYears));
    dispatch(OpportunityActions.opportunityDetailRequest(event.selectedYears));
    handleFilter(event);
  }, [dispatch, handleFilter]);

  return (
    <div style={{ height: '89vh' }}>
      <div id="opp_year_selector">
        <YearSelector
          selectedYears={selectedYears}
          label={label}
          onChange={handleYear}
        />
      </div>

      <div className="wrapper" style={{ height: 'calc(100% - 40px)' }}>
        <div className="right">
          <Grid container>
            <Grid size={7}>
              <div style={{ width: '100%' }}>
                <TopChart
                  summaryData={summaryData}
                  selectedYears={selectedYears}
                  selectedTopLeftItems={selectedTopLeftItems}
                  selectedTopLeftLabels={selectedTopLeftLabels}
                  defaultStartMonth={defaultStartMonth}
                  defaultMonth={defaultMonth}
                  handleFilter={handleFilter}
                />
              </div>
            </Grid>
            <Grid size={5}>
              <div style={{ width: '100%' }}>
                <TopRightChart
                  summaryData={summaryData}
                  selectedYears={selectedYears}
                  selectedTopRightItems={selectedTopRightItems}
                  selectedTopLeftItems={selectedTopLeftItems}
                  defaultStartMonth={defaultStartMonth}
                  defaultMonth={defaultMonth}
                  handleFilter={handleFilter}
                />
              </div>
            </Grid>
          </Grid>
          <Grid container spacing={1} style={{ height: 'calc(100% - 280px)' }}>
            <Grid size={6} style={{ height: '100%' }}>
              <div style={{ width: '100%' }}>
                <BottomChart
                  detailData={detailData}
                  selectedTopLeftItems={selectedTopLeftItems}
                  handleFilter={handleFilter}
                  Probability={Probability}
                  selectedSt={selectedSt}
                  searchStr={searchStr}
                />
              </div>
            </Grid>

            <Grid size={6} style={{ height: '100%' }}>
              <div style={{ width: '100%' }}>
                <BottomRightChart
                  detailData={detailData}
                  selectedTopRightItems={selectedTopRightItems}
                  handleFilter={handleFilter}
                  Probability={Probability}
                  selectedType={selectedType}
                  selectedDirector={selectedDirector}
                  searchRightStr={searchRightStr}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </div>

      {bubbleFlag && opportunitiesSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={opportunitiesSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

Opportunities.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  opportunitiesSetting: PropTypes.object.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  bubbleFlag: PropTypes.bool.isRequired,
};

export default Opportunities;
